import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import styled from 'styled-components';
import 'katex/dist/katex.min.css';

const MessageRenderer = ({ content }) => {
  return (
    <MarkdownContainer>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex]}
        components={{
          // Customize code blocks
          code({ inline, className, children, ...props }) {
            return inline ? (
              <InlineCode {...props}>{children}</InlineCode>
            ) : (
              <CodeBlock {...props} className={className}>
                {children}
              </CodeBlock>
            );
          },
          // Customize links
          a({ children, ...props }) {
            return (
              <StyledLink {...props} target="_blank" rel="noopener noreferrer">
                {children}
              </StyledLink>
            );
          },
          // Customize headings
          h1({ children, ...props }) {
            return <StyledH1 {...props}>{children}</StyledH1>;
          },
          h2({ children, ...props }) {
            return <StyledH2 {...props}>{children}</StyledH2>;
          },
          h3({ children, ...props }) {
            return <StyledH3 {...props}>{children}</StyledH3>;
          },
          // Customize blockquotes
          blockquote({ children, ...props }) {
            return <StyledBlockquote {...props}>{children}</StyledBlockquote>;
          },
          // Customize lists
          ul({ children, ...props }) {
            return <StyledUL {...props}>{children}</StyledUL>;
          },
          ol({ children, ...props }) {
            return <StyledOL {...props}>{children}</StyledOL>;
          },
          // Customize paragraphs
          p({ children, ...props }) {
            return <StyledP {...props}>{children}</StyledP>;
          },
          // Customize tables
          table({ children, ...props }) {
            return (
              <TableWrapper>
                <StyledTable {...props}>{children}</StyledTable>
              </TableWrapper>
            );
          },
          th({ children, ...props }) {
            return <StyledTH {...props}>{children}</StyledTH>;
          },
          td({ children, ...props }) {
            return <StyledTD {...props}>{children}</StyledTD>;
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </MarkdownContainer>
  );
};

const MarkdownContainer = styled.div`
  color: var(--text-primary);
  line-height: 1.6;
  word-wrap: break-word;
  
  /* KaTeX math rendering styles */
  .katex {
    font-size: 1.1em;
  }
  
  .katex-display {
    margin: 1rem 0;
    overflow-x: auto;
    overflow-y: hidden;
  }
`;

const InlineCode = styled.code`
  background-color: rgba(0, 0, 0, 0.1);
  padding: 0.2em 0.4em;
  border-radius: 3px;
  font-family: 'Monaco', 'Courier New', monospace;
  font-size: 0.9em;
`;

const CodeBlock = styled.pre`
  background-color: rgba(0, 0, 0, 0.1);
  padding: 1rem;
  border-radius: 6px;
  overflow-x: auto;
  margin: 0.5rem 0;
  
  code {
    font-family: 'Monaco', 'Courier New', monospace;
    font-size: 0.9em;
    line-height: 1.5;
  }
`;

const StyledLink = styled.a`
  color: var(--accent-color);
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const StyledH1 = styled.h1`
  font-size: 1.8em;
  margin: 0.8em 0 0.5em 0;
  color: var(--text-primary);
  border-bottom: 2px solid var(--card-border);
  padding-bottom: 0.3em;
`;

const StyledH2 = styled.h2`
  font-size: 1.5em;
  margin: 0.7em 0 0.4em 0;
  color: var(--text-primary);
  border-bottom: 1px solid var(--card-border);
  padding-bottom: 0.2em;
`;

const StyledH3 = styled.h3`
  font-size: 1.25em;
  margin: 0.6em 0 0.3em 0;
  color: var(--text-primary);
`;

const StyledBlockquote = styled.blockquote`
  border-left: 4px solid var(--accent-color);
  margin: 0.5rem 0;
  padding: 0.5rem 1rem;
  background-color: rgba(0, 0, 0, 0.05);
  color: var(--text-secondary);
  
  p {
    margin: 0.5rem 0;
  }
`;

const StyledUL = styled.ul`
  margin: 0.5rem 0;
  padding-left: 2rem;
  
  li {
    margin: 0.25rem 0;
  }
`;

const StyledOL = styled.ol`
  margin: 0.5rem 0;
  padding-left: 2rem;
  
  li {
    margin: 0.25rem 0;
  }
`;

const StyledP = styled.p`
  margin: 0.5rem 0;
  line-height: 1.6;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  margin: 1rem 0;
`;

const StyledTable = styled.table`
  border-collapse: collapse;
  margin: 0;
  width: 100%;
  display: table;
  
  /* Wrapper will handle overflow */
  & {
    table-layout: auto;
  }
`;

const StyledTH = styled.th`
  border: 1px solid var(--card-border);
  padding: 0.5rem;
  background-color: rgba(0, 0, 0, 0.1);
  font-weight: 600;
  text-align: left;
`;

const StyledTD = styled.td`
  border: 1px solid var(--card-border);
  padding: 0.5rem;
`;

export default MessageRenderer;
